// Lib
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { captureException } from '@sentry/browser';

// Components
import Spinner from '@components/Spinner';
import { cn } from '@components/lib/utils';

// Utils
import { createSanitizedMarkup } from '@utils/sanitize';

// Config
import { TIKTOK_OEMBED_URL } from '@config/urls';

const TIKTOK_OEMBED_BASE_URL = TIKTOK_OEMBED_URL;

interface TikTokPost {
  thumbnail?: {
    url?: string;
  };
  media?: {
    url?: string;
  };
}

interface TiktokPlayerProps {
  post: TikTokPost;
}

interface TikTokOEmbedResponse {
  status_msg?: string;
  html?: string;
}

const TiktokPlayer: React.FC<TiktokPlayerProps> = ({ post }) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [_error, setError] = useState<Error | boolean>(false);
  const [scriptSrc, setScriptSrc] = useState<string | false>(false);
  const [html, setHTML] = useState<string | false>(false);
  const ref = useRef<HTMLDivElement>(null);

  const previewUrl = post?.thumbnail?.url;
  const url = post?.media?.url;

  /**
   * Trigger loaded state when the iframe has loaded
   */
  React.useEffect(function () {
    if (!('MutationObserver' in window)) return setLoaded(true);
    const elem = ref.current;
    const observer = new MutationObserver(function (mutationList) {
      // Get the iframe from the mutation that has added it
      const iframeAdded = mutationList.reduce<HTMLIFrameElement | undefined>(
        function (acc, curr) {
          const iframe = Array.from(curr.addedNodes).find(function (node) {
            return node.nodeName === 'IFRAME';
          }) as HTMLIFrameElement | undefined;
          if (iframe) {
            acc = iframe;
          }
          return acc;
        },
        undefined,
      );
      if (iframeAdded) {
        iframeAdded.addEventListener('load', function () {
          return setLoaded(true);
        });
      }
    });
    if (elem) {
      observer.observe(elem, {
        childList: true,
        attributes: true,
        subtree: true,
      });
    }
    return function () {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    fetch(TIKTOK_OEMBED_BASE_URL + '?url=' + url)
      .then(function (res) {
        return res.json();
      })
      .then(function (res: TikTokOEmbedResponse) {
        if (res && res.status_msg) throw new Error(res.status_msg);
        if (!res || !res.html)
          throw new Error("API response doesn't look right");
        const htmlString = res.html;
        const tempElement = document.createElement('div');
        tempElement.innerHTML = htmlString;
        const scriptTag = tempElement.getElementsByTagName('script')[0];
        setScriptSrc(scriptTag && scriptTag.src);
        setHTML(htmlString.substr(0, htmlString.indexOf('<script')));
      })
      .catch(function (err: Error) {
        captureException(err);
        return setError(err);
      });
  }, [url]);

  return (
    <div className="relative">
      <Helmet>
        <script id="ttEmbedder" async={true} src={scriptSrc || undefined} />
      </Helmet>
      <div className="relative h-full bg-white after:absolute after:inset-0 after:opacity-100 after:transition-all after:duration-500 after:content-['']">
        <div className="mx-auto min-w-[325px] max-w-[482px] rounded-lg border border-[rgba(22,24,35,0.12)] max-[1024px]:max-w-[325px]">
          <div className="absolute inset-x-0 bottom-0 top-0 flex items-center justify-center">
            <Spinner />
          </div>
          <img
            src={previewUrl}
            alt=""
            className="max-h-[501px] max-w-[480px] object-contain max-[1024px]:max-h-[574px] max-[1024px]:max-w-[325px]"
          />
          <div className="h-[127px]" />
        </div>
      </div>
      <div
        ref={ref}
        className={cn(
          'absolute inset-x-0 -bottom-[18px] -top-[18px]',
          loaded && !!html ? 'flex' : 'hidden',
        )}
        dangerouslySetInnerHTML={createSanitizedMarkup(html || '', {
          ALLOWED_TAGS: ['blockquote', 'section', 'a', 'span', 'div'],
          ALLOWED_ATTR: [
            'cite',
            'class',
            'data-video-id',
            'style',
            'href',
            'target',
            'rel',
          ],
          ALLOW_DATA_ATTR: true,
        })}
      />
    </div>
  );
};

export default TiktokPlayer;
